import { AppliedCouponType, useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useLocale } from "lib/hooks/useLocale";
import { CouponType, SavingItemsType } from "lib/types/checkout";
import React, { useMemo, useState } from "react";
import AppliedItem from "./AppliedItem";
import { CaretDown, Coins } from "@phosphor-icons/react";
import { Icon } from "@shopflo/ui/icon";
import { classNames } from "lib/utils/helpers";
import { currencyFormatter } from "lib/utils/formatters";
import { AnimatePresence, motion } from "motion/react";
import { DiscountConfigType } from "lib/types/merchantConfigTypes";
import { hexToRgba } from "@shopflo/ui/utils";

interface CouponSummaryStripProps {
  showRewards?: boolean;
  showSavings?: boolean;
  showAppliedCoupons?: boolean;
  savingItems: SavingItemsType;
  couponDisplayType?: string;
  handleDeleteCoupon: (couponCode: string) => void;
  defaultOpen?: boolean;
  showIcon?: boolean;
  parent?: "DIALOG" | "CHECKOUT";
  customisations?: DiscountConfigType;
}

const CouponSummaryStrip: React.FC<CouponSummaryStripProps> = ({
  showRewards = false,
  showSavings = true,
  showAppliedCoupons = false,
  savingItems,
  couponDisplayType,
  handleDeleteCoupon,
  defaultOpen = false,
  showIcon = true,
  parent = "CHECKOUT",
  customisations,
}) => {
  const { t } = useLocale();
  const {
    state: { appliedCoupons, shippingHandles },
  } = useCheckoutContext();

  const [couponInputEnabled, setCouponInputEnabled] = useState<boolean>(defaultOpen);

  const { count, manualFreebieCount, savings } = savingItems;
  const isBankOfferApplied = appliedCoupons?.some((coupon) => coupon.couponType === CouponType.PAYMENT_OFFER);
  const isShippingDiscountApplied = appliedCoupons?.some(
    (coupon) => coupon.couponType === CouponType.SHIPPING,
  );
  const selectedShippingHandle = shippingHandles?.find((handle) => handle?.selected_handle);

  const rewards = appliedCoupons?.filter((coupon) => coupon.isReward) || [];
  const appliedCouponsVisible =
    isBankOfferApplied || showAppliedCoupons || Boolean(couponDisplayType !== "STRIP");

  const visibleAppliedCoupons = useMemo(() => {
    return appliedCoupons?.filter(
      (coupon) =>
        !(coupon?.isFreebie && coupon?.autoApplied && !coupon?.isRemovable) &&
        !coupon?.isReward &&
        (parent === "DIALOG" ? !coupon?.isProductSelector : true),
    );
  }, [appliedCoupons, parent]);

  const renderTotalSavings = () => {
    if (!savings) {
      if (isBankOfferApplied) {
        return t("bank_offer_applied");
      }
      if (isShippingDiscountApplied) {
        const originalPrice = parseFloat(selectedShippingHandle?.original_price || "0");
        const currentPrice = parseFloat(selectedShippingHandle?.price || "0");
        return currencyFormatter(originalPrice - currentPrice);
      }
      return t("automatic_freebies_free_text");
    } else {
      return currencyFormatter(savings);
    }
  };

  if (
    savings === 0 &&
    couponDisplayType === "STRIP" &&
    !showRewards &&
    manualFreebieCount === 0 &&
    !isBankOfferApplied
  ) {
    return <></>;
  }

  if (rewards.length === 0 && !(showSavings && count > 0) && visibleAppliedCoupons.length === 0) {
    return <></>;
  }

  const savingsGradient = customisations?.totalSavings?.primaryColor
    ? `linear-gradient(to right, ${hexToRgba(customisations.totalSavings.primaryColor, 0.1)}, ${hexToRgba(customisations.totalSavings.primaryColor, 0.05)}, ${hexToRgba(customisations.totalSavings.primaryColor, 0)})`
    : "";

  return (
    <div
      className={classNames(
        "flex w-full flex-col px-3",
      )}
      style={{
        background: savingsGradient,
        backgroundColor: customisations?.totalSavings?.backgroundColor,
      }}>
      <div
        className="flex w-full flex-col cursor-pointer "
        onClick={() => {
          if (appliedCouponsVisible) {
            setCouponInputEnabled(!couponInputEnabled);
          }
        }}>
        {showRewards &&
          rewards.map((reward) => (
            <div
              className="flex items-center space-x-2 px-4 py-3 text-text-sm-medium text-yay-dark"
              key={reward.code}
            >
              <span className="flex h-4 w-4 items-center justify-center rounded-full border border-[#2C874A80] text-text-xxs-regular"
              >
                ₹
              </span>
              <span style={{ color: customisations?.totalSavings?.primaryColor }}>{reward?.rewardData?.header}</span>
            </div>
          ))}
        {showSavings && count > 0 && (
          <div className="flex justify-between items-center overflow-hidden  py-2.5">
            <div className="flex gap-1.5 items-center">
              {showIcon && (
                <Icon
                  IconComponent={Coins}
                  size="xs"
                  className="text-yellow-500"
                  iconProps={{ weight: "fill", color: customisations?.totalSavings?.primaryColor }}
                />
              )}
              <p className="text-text-xs-semibold text-zinc-700"
                style={{
                  color: customisations?.totalSavings?.infoColor,
                }}
              >{renderTotalSavings()}</p>
              {!isBankOfferApplied && (
                <p className="text-text-xs-medium text-zinc-700"
                  style={{
                    color: customisations?.totalSavings?.infoColor,
                  }}
                >
                  {t("saved_with_discounts")}
                </p>
              )}
            </div>

            {appliedCouponsVisible && Boolean(visibleAppliedCoupons?.length) && (
              <div className="flex gap-1 justify-end">
                <Icon
                  IconComponent={CaretDown}
                  size="xxs"
                  className={classNames(
                    "text-zinc-500 transition-transform duration-200 ease-out",
                    couponInputEnabled ? "rotate-180" : "rotate-0",
                  )}
                  iconProps={{ weight: "bold", color: customisations?.totalSavings?.infoColor }}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <AnimatePresence initial={false} mode="wait">
        {couponInputEnabled && (
          <motion.div
            key="coupon-list"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ type: "spring", stiffness: 350, damping: 30, mass: 1 }}
            className="overflow-hidden">
            <div className="pb-2">
              {visibleAppliedCoupons?.map((coupon: AppliedCouponType, index: number) => (
                <AppliedItem
                  key={`${coupon?.code}_${index}`}
                  coupon={coupon}
                  handleDeleteItem={handleDeleteCoupon}
                  customisations={customisations}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CouponSummaryStrip;
